/**
 * RuleFormDialog (FE-4, PHASE1 §7.4). Create or edit a rule: name, priority,
 * match mode, conditions (RuleConditionsEditor), actions (bucket / rename /
 * tags), and the stop-processing / active flags. Client-side validation mirrors
 * the `rule_invalid` contract (≥1 condition with a value, ≥1 action). A live
 * dry-run preview is embedded.
 */
import { useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { BucketSelect } from '../../components/ui/BucketSelect.tsx';
import { SegmentedControl } from '../../components/ui/SegmentedControl.tsx';
import { TagInput } from '../../components/ui/TagInput.tsx';
import { RuleConditionsEditor } from './RuleConditionsEditor.tsx';
import { RuleDryRunPanel } from './RuleDryRunPanel.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import {
  blankCondition,
  fromEditableConditions,
  toEditableConditions,
  useCreateRule,
  useUpdateRule,
  type EditableCondition,
} from '../../lib/hooks/useRules.ts';
import type { RuleCreate, RuleMatchMode, RuleOut } from '../../lib/types.ts';

const MATCH_MODE_OPTIONS: { value: RuleMatchMode; label: string }[] = [
  { value: 'all', label: 'Alle (UND)' },
  { value: 'any', label: 'Beliebige (ODER)' },
];

interface Draft {
  name: string;
  priority: string;
  matchMode: RuleMatchMode;
  conditions: EditableCondition[];
  setBucketId: number | null;
  renamePayee: string;
  addTags: string[];
  stopProcessing: boolean;
  active: boolean;
}

function buildDraft(rule?: RuleOut | null, initial?: RuleCreate | null): Draft {
  const source = rule ?? initial ?? null;
  if (source) {
    return {
      name: source.name,
      priority: String(source.priority ?? 100),
      matchMode: source.match_mode ?? 'all',
      conditions:
        source.conditions.length > 0 ? toEditableConditions(source.conditions) : [blankCondition()],
      setBucketId: source.actions.set_bucket_id ?? null,
      renamePayee: source.actions.rename_payee ?? '',
      addTags: source.actions.add_tags ?? [],
      stopProcessing: source.stop_processing ?? false,
      active: source.active ?? true,
    };
  }
  return {
    name: '',
    priority: '100',
    matchMode: 'all',
    conditions: [blankCondition()],
    setBucketId: null,
    renamePayee: '',
    addTags: [],
    stopProcessing: false,
    active: true,
  };
}

export interface RuleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present → edit mode. */
  rule?: RuleOut | null;
  /** Present → create mode prefilled (e.g. from a transaction). */
  initial?: RuleCreate | null;
}

export function RuleFormDialog({ open, onOpenChange, rule, initial }: RuleFormDialogProps) {
  const { toast } = useToast();
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const [draft, setDraft] = useState<Draft>(() => buildDraft(rule, initial));
  const [formError, setFormError] = useState<string | null>(null);

  // Re-seed the form when the dialog opens or switches target, without an
  // effect: adjust state during render off a signature (React docs pattern).
  const seedKey = open ? (rule ? `r${rule.id}` : `i:${initial?.name ?? 'new'}`) : 'closed';
  const [lastSeedKey, setLastSeedKey] = useState('closed');
  if (seedKey !== lastSeedKey) {
    setLastSeedKey(seedKey);
    if (open) {
      setDraft(buildDraft(rule, initial));
      setFormError(null);
    }
  }

  const cleanedConditions = fromEditableConditions(draft.conditions);
  const hasAction =
    draft.setBucketId != null || draft.renamePayee.trim() !== '' || draft.addTags.length > 0;
  const pending = createRule.isPending || updateRule.isPending;

  function patch(next: Partial<Draft>) {
    setDraft((prev) => ({ ...prev, ...next }));
  }

  function submit() {
    if (!draft.name.trim()) {
      setFormError('Bitte einen Namen angeben.');
      return;
    }
    if (cleanedConditions.length === 0) {
      setFormError('Mindestens eine Bedingung mit einem Wert ist erforderlich.');
      return;
    }
    if (!hasAction) {
      setFormError('Mindestens eine Aktion (Kategorie, Umbenennung oder Tags) ist erforderlich.');
      return;
    }
    setFormError(null);

    // `|| 100` would coerce the valid priority 0 (reorder puts the top rule at
    // 0) to the default — only unparseable input falls back to 100.
    const parsedPriority = Number.parseInt(draft.priority, 10);

    const body: RuleCreate = {
      name: draft.name.trim(),
      match_mode: draft.matchMode,
      conditions: cleanedConditions,
      actions: {
        set_bucket_id: draft.setBucketId,
        rename_payee: draft.renamePayee.trim() || null,
        add_tags: draft.addTags,
      },
      priority: Number.isNaN(parsedPriority) ? 100 : parsedPriority,
      stop_processing: draft.stopProcessing,
      active: draft.active,
    };

    const onSuccess = () => {
      toast(rule ? 'Regel aktualisiert.' : 'Regel erstellt.', { variant: 'success' });
      onOpenChange(false);
    };
    const onError = (err: Error) => setFormError(err.message);

    if (rule) {
      updateRule.mutate({ id: rule.id, body }, { onSuccess, onError });
    } else {
      createRule.mutate(body, { onSuccess, onError });
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={rule ? 'Regel bearbeiten' : 'Neue Regel'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
            Abbrechen
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending ? 'Speichere …' : 'Speichern'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Field label="Name" required>
              {(id) => (
                <Input
                  id={id}
                  value={draft.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="z. B. Supermarkt → Lebensmittel"
                />
              )}
            </Field>
          </div>
          <Field label="Priorität" hint="Kleiner = zuerst">
            {(id) => (
              <Input
                id={id}
                type="number"
                value={draft.priority}
                onChange={(e) => patch({ priority: e.target.value })}
              />
            )}
          </Field>
        </div>

        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Verknüpfung
          </span>
          <SegmentedControl
            value={draft.matchMode}
            onChange={(value) => patch({ matchMode: value })}
            options={MATCH_MODE_OPTIONS}
            ariaLabel="Verknüpfung der Bedingungen"
          />
        </div>

        <div className="space-y-3">
          <span className="block text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
            Bedingungen
          </span>
          <RuleConditionsEditor
            conditions={draft.conditions}
            onChange={(conditions) => patch({ conditions })}
          />
        </div>

        <div className="space-y-3">
          <span className="block text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
            Aktionen
          </span>
          <Field label="Kategorie setzen">
            {(id) => (
              <BucketSelect
                id={id}
                value={draft.setBucketId}
                onChange={(bucketId) => patch({ setBucketId: bucketId })}
                allowClear
                placeholder="Keine Kategorie"
              />
            )}
          </Field>
          <Field label="Empfänger umbenennen">
            {(id) => (
              <Input
                id={id}
                value={draft.renamePayee}
                onChange={(e) => patch({ renamePayee: e.target.value })}
                placeholder="Optionaler neuer Empfängername"
              />
            )}
          </Field>
          <Field label="Tags hinzufügen">
            {(id) => (
              <TagInput id={id} value={draft.addTags} onChange={(addTags) => patch({ addTags })} />
            )}
          </Field>
        </div>

        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500"
              checked={draft.stopProcessing}
              onChange={(e) => patch({ stopProcessing: e.target.checked })}
            />
            Weitere Regeln nach dieser stoppen
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500"
              checked={draft.active}
              onChange={(e) => patch({ active: e.target.checked })}
            />
            Regel aktiv
          </label>
        </div>

        <RuleDryRunPanel matchMode={draft.matchMode} conditions={cleanedConditions} />

        {formError ? <p className="text-sm text-rose-600 dark:text-rose-400">{formError}</p> : null}
      </div>
    </Modal>
  );
}
